// Taken from: https://steve.dignam.xyz/2020/03/31/practical-ordering/

const START_CHAR_CODE = 32
const END_CHAR_CODE = 126
export const FIRST_POSITION = String.fromCharCode(START_CHAR_CODE + 1)

function assertDev(expr: boolean) {
  if (!expr) {
    throw Error("Assertion Error")
  }
}

export function comparePositions(firstPos: string, secondPos: string) {
  return +(firstPos < secondPos) - +(firstPos > secondPos)
}

export function isValidPosition(pos: string) {
  if (pos === "" || pos.charCodeAt(pos.length - 1) == START_CHAR_CODE) {
    return false
  }
  for (let i = 0; i < pos.length; i++) {
    const t = pos.charCodeAt(i)
    if (t < START_CHAR_CODE || t > END_CHAR_CODE) {
      return false
    }
  }
  return true
}

export function positionBefore(pos: string) {
  assertDev(0 !== pos.length)

  for (let i = pos.length - 1; i >= 0; i--) {
    let curCharCode = pos.charCodeAt(i)
    if (curCharCode > START_CHAR_CODE + 1) {
      let position = pos.substr(0, i) + String.fromCharCode(curCharCode - 1)
      assertDev(isValidPosition(position))
      return position
    }
  }
  let position =
    pos.substr(0, pos.length - 1) +
    String.fromCharCode(START_CHAR_CODE) +
    String.fromCharCode(END_CHAR_CODE)
  assertDev(isValidPosition(position))
  return position
}

export function positionAfter(pos: string) {
  assertDev(0 !== pos.length)

  for (let i = pos.length - 1; i >= 0; i--) {
    let curCharCode = pos.charCodeAt(i)
    if (curCharCode < END_CHAR_CODE) {
      let position = pos.substr(0, i) + String.fromCharCode(curCharCode + 1)
      assertDev(isValidPosition(position))
      return position
    }
  }
  let position = pos + String.fromCharCode(START_CHAR_CODE + 1)
  assertDev(isValidPosition(position))
  return position
}

function avg(a: number, b: number): number {
  return Math.trunc((a + b) / 2)
}

export function positionBetween(firstPos: string, secondPos: string) {
  assertDev(firstPos < secondPos)
  let flag = false
  let position = ""
  const maxLength = Math.max(firstPos.length, secondPos.length)
  for (let i = 0; i < maxLength; i++) {
    const lower = i < firstPos.length ? firstPos.charCodeAt(i) : START_CHAR_CODE
    const upper =
      i < secondPos.length && !flag ? secondPos.charCodeAt(i) : END_CHAR_CODE
    if (lower === upper) {
      position += String.fromCharCode(lower)
    } else if (upper - lower > 1) {
      position += String.fromCharCode(avg(lower, upper))
      flag = false
      break
    } else {
      position += String.fromCharCode(lower)
      flag = true
    }
  }

  if (flag) {
    position += String.fromCharCode(avg(START_CHAR_CODE, END_CHAR_CODE))
  }
  assertDev(firstPos < position)
  assertDev(position < secondPos)
  assertDev(isValidPosition(position))
  return position
}

// An item's position can either string or null.
export type Position = string | null

export type PositionedObj = Record<string, any>

// We want to constrain an item's "position" key to valid Positions.
export type StringOrNullKeys<T> = Extract<
  {
    [K in keyof T]: T[K] extends Position ? K : never
  }[keyof T],
  string
>

export type PositionOptions<T extends PositionedObj> = {
  list: T[]
  item: T
  index: number
  positionKey?: StringOrNullKeys<T>
} & (T extends { position: Position }
  ? { positionKey?: StringOrNullKeys<T> } // If item has a "position" key, positionKey is optional
  : { positionKey: StringOrNullKeys<T> }) // If item lacks a "position" key, positionKey is required

function getItemPosition<T extends PositionedObj>(
  item: T | undefined,
  positionKey: StringOrNullKeys<T>
): string | null {
  const raw = item?.[positionKey]
  return typeof raw === "string" && isValidPosition(raw) ? raw : null
}

function getPositionOrEmpty<T extends PositionedObj>(
  item: T | undefined,
  positionKey: StringOrNullKeys<T>
): string {
  return getItemPosition(item, positionKey) || ""
}

function reorderList<T>(list: T[], item: T, index: number): T[] {
  const itemIndex = list.findIndex((listItem) => listItem === item)
  if (itemIndex === -1) {
    return list
  }

  const reordered = [...list]
  reordered.splice(itemIndex, 1)
  reordered.splice(index, 0, item)
  return reordered
}

type PositionUpdate<T extends PositionedObj> = {
  item: T
  position: string
}

// Sometimes the user may be moving an item to a position in the list
// where the items that come before it are not yet positioned.
// This could occur in a list that is a mix of positioned items and
// unpositioned items (the positioned items should always be sorted)
// to the front of the list).
//
// `getPositionUpdates` ensures that all items preceding the provided
// item are positioned (either already or will be assigned a position).
export function getPositionUpdates<T extends PositionedObj>(
  options: PositionOptions<T>
): PositionUpdate<T>[] {
  const opts = withDefaultPositionKey(options)
  const { list, item, index } = opts
  const positionKey = opts.positionKey as StringOrNullKeys<T>
  const reorderedList = reorderList(list, item, index)

  // NOTE: shallow copying the list since we'll potentially modify the item's
  // position when generating the list of positions.
  type PositionedItem = T & Record<StringOrNullKeys<T>, Position>
  const copiedList = reorderedList.map((listItem) => ({
    ...listItem
  })) as PositionedItem[]

  // Get the index of the last positioned item in the list (likely upstream from
  // provided index). We'll traverse from the upstreamIndex to the provided index
  // generating positions along the way.
  const upstreamIndex =
    copiedList.findLastIndex((listItem) =>
      getItemPosition(listItem, positionKey)
    ) +
    // Pad by 1, as we want to start positioning the index after the first item
    // that has a position.
    1

  // Ensure we're starting at the furthest upstream index.
  // Since we're padding by 1 above, this may have pushed the upstreamIndex past the index.
  // We basically only need to derive multiple positions if we are truly dealing with
  // upstream items that lack a position.
  if (upstreamIndex < index) {
    // Start with the upstreamIndex working down the list till we reach the provided index.
    const items = reorderedList.slice(upstreamIndex, index + 1)

    // Now reduce the items into position updates.
    return items.reduce((acc, listItem, i) => {
      const slotIndex = upstreamIndex + i
      const position = getPosition({
        list: copiedList,
        item: copiedList[slotIndex],
        index: slotIndex,
        positionKey
      } as PositionOptions<T>)

      if (position !== null) {
        const typedItem = copiedList[slotIndex] as Record<
          StringOrNullKeys<T>,
          Position
        >
        // Side-effect: update the position of the listItem so we can compare
        // against it in next iteration.
        typedItem[positionKey] = position
        acc.push({ item: listItem, position })
      }

      return acc
    }, [] as PositionUpdate<T>[])
  }

  const position = getPosition(options)
  return position !== null ? [{ item, position }] : []
}

// Assumes list already contains item at index with position (likely in local state)
// and we merely want to determine the item's updated position (possibly for database mutation).
export function getPosition<T extends PositionedObj>(
  options: PositionOptions<T>
): Position {
  const opts = withDefaultPositionKey(options)
  const { list, item, index } = opts
  const positionKey = opts.positionKey as StringOrNullKeys<T>

  const targetItem = list[index]
  // The index of the item being moved which can be compared to the target index
  // which can be used to determine previous and next positions when moving item
  // in between items.
  const itemIndex = list.findIndex(
    (listItem) =>
      // We cannot compare item positions because there may be multiple items with
      // a null position (which is valid). Let's just assume referential equality.
      listItem === item
  )

  // Inserted at beginning of list.
  if (index === 0) {
    const targetPosition = getItemPosition(targetItem, positionKey)
    if (targetPosition) return positionBefore(targetPosition)
    return FIRST_POSITION
  }
  // Inserted at end of list.
  if (index === list.length - 1) {
    const targetPosition = getItemPosition(targetItem, positionKey)
    if (targetPosition) return positionAfter(targetPosition)
  }
  // Inserted in between items in list.
  if (index < itemIndex) {
    // Going upstream in list, need new position in between upstream index and target index.
    const prevPosition = getPositionOrEmpty(list[index - 1], positionKey)
    const nextPosition = getPositionOrEmpty(list[index], positionKey)
    // Only position between when we have a next position (assuming we always haeve a prev position).
    return nextPosition
      ? positionBetween(prevPosition, nextPosition)
      : positionAfter(prevPosition)
  }
  if (index > itemIndex) {
    // Going downstream in list, need new position in between target index and downstream index.
    const prevPosition = getPositionOrEmpty(list[index], positionKey)
    const nextPosition = getPositionOrEmpty(list[index + 1], positionKey)
    // Only position between when we have a next position (assuming we always haeve a prev position).
    return nextPosition
      ? positionBetween(prevPosition, nextPosition)
      : positionAfter(prevPosition)
  }
  // Not moving in list, but we may be setting a position.
  if (index === itemIndex) {
    if (item) {
      const existingPosition = getItemPosition(item, positionKey)
      // When already positioned, return current position.
      if (existingPosition) {
        return existingPosition
      } else {
        // Otherwise, attempt to position the item after the previous one
        const prevPosition = getPositionOrEmpty(list[index - 1], positionKey)
        return prevPosition ? positionAfter(prevPosition) : null
      }
    }
    return FIRST_POSITION
  }

  // Unable to position item.
  return null
}

// Returns position strings for each item updated by `getPositionUpdates`.
export function getPositions<T extends PositionedObj>(
  options: PositionOptions<T>
): string[] {
  return getPositionUpdates(options).map(({ position }) => position)
}

// Sort objs by position, taking into account null positions, sorting them last.
// This can be used to order the objs in state or writing the list into the cache.
// Its too complex to automatiacally update the cache for queries, so not providing
// that out of the box.

// Optionally require the positionKey when the list items' positionKey
// is "position".
export function sortByPosition<
  T extends PositionedObj & { position: Position }
>(
  list: PositionOptions<T>["list"],
  positionKey?: PositionOptions<T>["positionKey"]
): PositionOptions<T>["list"]
// Require the positionKey otherwise.
export function sortByPosition<T extends PositionedObj>(
  list: PositionOptions<T>["list"],
  positionKey: PositionOptions<T>["positionKey"]
): PositionOptions<T>["list"]
export function sortByPosition<T extends PositionedObj>(
  list: PositionOptions<T>["list"],
  positionKey: StringOrNullKeys<T> = "position" as StringOrNullKeys<T>
): PositionOptions<T>["list"] {
  // Cannot sort empty list.
  if (!list?.length) {
    return list
  }

  // 1) If an explicit key was passed, use it.
  // 2) Otherwise, if the first item has a "position" property, default to that.
  // 3) Otherwise, error out—user must supply a key.
  let key: StringOrNullKeys<T>
  if (positionKey) {
    key = positionKey
  } else if ("position" in list[0]) {
    key = "position" as StringOrNullKeys<T>
  } else {
    throw new Error(
      `sortByPosition: you must provide a positionKey when your items don't have a "position" field`
    )
  }

  return list.sort((objA, objB) => {
    const positionA = getItemPosition(objA, key)
    const positionB = getItemPosition(objB, key)

    // When both items have a position, we can order lexicographically by them.
    return positionA && positionB
      ? positionA < positionB
        ? -1
        : positionA > positionB
        ? 1
        : 0
      : // When only one item has a position, prefer that item.
      positionA
      ? -1
      : positionB
      ? 1
      : // When neither items have a position, leave as is.
        0
  })
}

// Assign the generated positions to the provided list (returning a new list, no side-effects).
export function assignPositions<T extends PositionedObj>(
  options: PositionOptions<T>
): T[] {
  const positionKey = withDefaultPositionKey(options)
    .positionKey as StringOrNullKeys<T>
  const positionByItem = new Map(
    getPositionUpdates(options).map(({ item, position }) => [item, position])
  )

  return options.list.map((listItem) => {
    const position = positionByItem.get(listItem)

    return position !== undefined
      ? {
          ...listItem,
          [positionKey]: position
        }
      : listItem
  })
}

// We need to "safely" provide a default positionKey when none is provied.
// Having trouble making TS happy with the assignment, so ignoring ts error for now.
function withDefaultPositionKey<T extends PositionedObj>(
  options: PositionOptions<T>
): PositionOptions<T> & { positionKey: StringOrNullKeys<T> } {
  let { item, positionKey } = options

  if (positionKey === undefined) {
    if ("position" in item) {
      // here we know item has a "position" key
      positionKey = "position" as StringOrNullKeys<T>
    } else {
      throw new Error(
        "sort/get/getPositions: you must supply a positionKey when your items don't have `.position`"
      )
    }
  }

  // now TS knows positionKey is definitely a valid key of T
  return {
    ...options,
    positionKey
  } as PositionOptions<T> & { positionKey: StringOrNullKeys<T> }
}

/**
 * For each item in `list` (assumed already in intended index order), ensure
 * `positionKey` is a valid fractional string. If an item’s `positionKey` is
 * already valid (isValidPosition), leave it; otherwise, generate a new string
 * that slots it correctly between its neighbors.
 */
type FillOptions<T extends Record<string, any>> = {
  list: T[]
  positionKey: keyof T
}

export function fillMissingPositions<T extends Record<string, any>>({
  list,
  positionKey
}: FillOptions<T>): (T & Record<string, any>)[] {
  const n = list.length
  if (n === 0) return []

  // Make a shallow copy so we don’t mutate the original objects
  const result = list.map((item) => ({ ...item })) as (T &
    Record<string, any>)[]

  // Helper to check if a value is already a valid fractional position
  const isValid = (val: any): val is string =>
    typeof val === "string" && isValidPosition(val)

  // Track the “last known” fractional position as we scan left→right.
  let lastPos: string | "" = ""

  for (let i = 0; i < n; i++) {
    const cur = result[i]
    const raw = cur[positionKey] as any

    if (isValid(raw)) {
      // Already a valid string—just update lastPos and continue
      lastPos = raw
      continue
    }

    // Find the next existing valid fractional position after index i
    let nextPos: string | "" = ""
    for (let j = i + 1; j < n; j++) {
      const candidate = result[j][positionKey] as any
      if (isValid(candidate)) {
        nextPos = candidate
        break
      }
    }

    let newPos: string
    if (lastPos && nextPos) {
      // We have a valid neighbor on each side
      newPos = positionBetween(lastPos, nextPos)
    } else if (nextPos) {
      // Only a next neighbor (this will become the new first slot)
      newPos = positionBefore(nextPos)
    } else if (lastPos) {
      // Only a last neighbor (this appends to the end)
      newPos = positionAfter(lastPos)
    } else {
      // No neighbors at all (first missing entry in an otherwise-empty list)
      newPos = FIRST_POSITION
    }

    // Assign and advance lastPos
    cur[positionKey] = newPos as any
    lastPos = newPos
  }

  return result
}

/**
 * Options for getPositionFromIndex:
 * - list: array of items (each item has a string|null at positionKey)
 * - index: zero-based slot where a new item should land
 * - positionKey: the field name holding the fractional string (default: "position")
 */
type GetPositionFromIndexOptions<T extends Record<string, any>> = {
  list: T[]
  index: number
  positionKey?: keyof T
}

/**
 * Given a list of items (with fractional strings in `positionKey` or null),
 * return the fractional‐index that, when assigned to a new item at `index`,
 * will cause it to sort exactly into that slot.
 */
export function getPositionFromIndex<T extends Record<string, any>>({
  list,
  index,
  positionKey = "position" as keyof T
}: GetPositionFromIndexOptions<T>): string {
  // 1) If the list is empty, just return FIRST_POSITION.
  if (list.length === 0) {
    return FIRST_POSITION
  }

  // 2) Inserting at the front (index === 0):
  //    If the first item already has a valid string, do positionBefore(that).
  //    Otherwise (no one has a valid string at all), just use FIRST_POSITION.
  if (index === 0) {
    const nextPos = list[0][positionKey] as any
    if (typeof nextPos === "string" && isValidPosition(nextPos)) {
      return positionBefore(nextPos)
    } else {
      return FIRST_POSITION
    }
  }

  // 3) For any index > 0, we can look at the “would-be previous” (i – 1)
  //    and “would-be next” (i) slots. If prev is missing or invalid,
  //    treat it like “no previous” (so go to FIRST_POSITION or before next).
  const rawPrev = list[index - 1]?.[positionKey] as any
  const prevPos =
    typeof rawPrev === "string" && isValidPosition(rawPrev)
      ? (rawPrev as string)
      : ""

  const rawNext = list[index]?.[positionKey] as any
  const nextPos =
    typeof rawNext === "string" && isValidPosition(rawNext)
      ? (rawNext as string)
      : ""

  // 3a) If there’s both a valid prevPos and nextPos, sit between them.
  if (prevPos && nextPos) {
    return positionBetween(prevPos, nextPos)
  }

  // 3b) If only prevPos exists, append after it.
  if (prevPos) {
    return positionAfter(prevPos)
  }

  // 3c) If only nextPos exists, put just before it.
  if (nextPos) {
    return positionBefore(nextPos)
  }

  // 3d) Neither exists (i.e. list had no valid strings in [0..index−1] or [index]),
  //     so just return FIRST_POSITION.
  return FIRST_POSITION
}
